import React, { useEffect, useState } from 'react';
import { useApplyScopeLock, useLocationOptions } from '../Admin/shared/useLocationOptions';
import { livestockActorsApi, ACTOR_TYPE_MAP, SEX_MAP, AGE_GROUP_MAP, STATUS_MAP } from '../../api/workforceApi';
import { useAuth } from '../../context/AuthContext';
import { logRegistryProposal } from '../../api/registryCentralApi';

function parseError(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

const INP =
  'w-full px-3.5 py-3 border border-gray-200 rounded-xl text-[13px] text-gray-700 bg-gray-50 ' +
  'focus:outline-none focus:border-green-400 focus:bg-white placeholder-gray-400 transition';
const LBL = 'block text-[13px] font-semibold text-gray-800 mb-1.5';

function Field({ label, required, hint, children }) {
  return (
    <div className="mb-4">
      <label className={LBL}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Select({ value, onChange, options, placeholder = 'Select…', disabled = false }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} disabled={disabled}
        className="w-full appearance-none px-3.5 py-3 border border-gray-200 rounded-xl text-[13px] text-gray-700 bg-gray-50 focus:outline-none focus:border-green-400 focus:bg-white transition cursor-pointer disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

const ACTOR_TYPE_OPTIONS = Object.entries(ACTOR_TYPE_MAP).map(([value, label]) => ({ value: label, label }));
const SEX_OPTIONS = Object.entries(SEX_MAP).map(([value, label]) => ({ value: label, label }));
const AGE_GROUP_OPTIONS = Object.entries(AGE_GROUP_MAP).map(([value, label]) => ({ value, label }));
const STATUS_OPTIONS = Object.entries(STATUS_MAP).map(([value, label]) => ({ value: label, label }));

const EMPTY = {
  fullName: '', actorType: '', sex: '', ageGroup: '', contactPhone: '',
  state: '', lga: '', status: 'Active',
};

// "+ Propose" popup for registering a new Livestock Actor inline from another
// module's Plan Owner / officer lookup (DCIC-16's registry-first pattern —
// this is a lightweight direct-create, not a review queue, matching how
// registry lookups elsewhere in the app resolve missing entries).
export default function ProposeActorModal({ isOpen, onClose, onCreated }) {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(f.state);
  const { lockedState, lockedLga } = useApplyScopeLock(f, setF, { enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setF({ ...EMPTY, state: lockedState, lga: lockedLga });
    setError(''); setBusy(false);
  }, [isOpen, lockedState, lockedLga]);

  if (!isOpen) return null;

  const set = (k) => (e) =>
    setF((p) => (k === 'state'
      ? { ...p, state: e?.target ? e.target.value : e, lga: '' }
      : { ...p, [k]: e?.target ? e.target.value : e }));

  const isValidPhone = /^(0[789]\d{9}|\+234[789]\d{8})$/.test(f.contactPhone.trim());
  const canCreate = f.fullName.trim().length >= 3 && !!f.actorType && !!f.sex && !!f.ageGroup &&
    isValidPhone && !!f.state && !!f.lga && !!f.status;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true); setError('');
    try {
      const record = await livestockActorsApi.create({
        fullName: f.fullName.trim(),
        contactPhone: f.contactPhone.trim(),
        actorType: f.actorType,
        sex: f.sex,
        ageGroup: f.ageGroup,
        state: f.state,
        lga: f.lga,
        status: f.status,
        verificationStatus: 'Pending',
      });
      logRegistryProposal('livestock_actor', { name: f.fullName, state: f.state, lga: f.lga }, user);
      onCreated?.(record);
      onClose();
    } catch (err) {
      setError(parseError(err, 'Failed to create actor record.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose New Actor</h3>
            <p className="text-xs text-gray-400 mt-0.5">Registry proposal — adds to Livestock Actor Registration</p>
          </div>
          <button type="button" onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 text-xl leading-none">×</button>
        </div>

        <div className="p-6">
          {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">{error}</div>}

          <Field label="Full Name" required hint="Minimum 3 characters">
            <input value={f.fullName} onChange={set('fullName')} placeholder="e.g. Amina Bello" className={INP} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Actor Type" required>
              <Select value={f.actorType} onChange={set('actorType')} options={ACTOR_TYPE_OPTIONS} placeholder="Select actor type" />
            </Field>
            <Field label="Status" required>
              <Select value={f.status} onChange={set('status')} options={STATUS_OPTIONS} placeholder="Select status" />
            </Field>
            <Field label="Sex" required>
              <Select value={f.sex} onChange={set('sex')} options={SEX_OPTIONS} placeholder="Select sex" />
            </Field>
            <Field label="Age Group" required>
              <Select value={f.ageGroup} onChange={set('ageGroup')} options={AGE_GROUP_OPTIONS} placeholder="Select age group" />
            </Field>
            <div className="col-span-2">
              <Field label="Phone" required hint="Nigerian 07/08/09 or +234, 11 digits">
                <input value={f.contactPhone} onChange={set('contactPhone')} placeholder="0803 123 4567" className={INP} />
              </Field>
            </div>
            <Field label="State" required>
              <Select value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state" disabled={Boolean(lockedState)} />
            </Field>
            <Field label="LGA" required>
              <Select value={f.lga} onChange={set('lga')} options={lgaOptions}
                placeholder={f.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
                disabled={Boolean(lockedLga) || !f.state || loadingLgas} />
            </Field>
          </div>

          <div className="flex gap-3 mt-2 pt-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-2xl border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 transition">Close</button>
            <button onClick={submit} disabled={!canCreate || busy}
              className={`flex-1 py-3 rounded-2xl text-[13px] font-semibold text-white transition ${canCreate && !busy ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              {busy ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
